import React, { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  GraduationCap,
  IndianRupee,
  Settings,
  School,
  X,
  UserPlus,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Bell,
  CalendarDays
} from 'lucide-react';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

interface NavLeaf {
  name: string;
  href: string;
}

interface NavItem {
  name: string;
  icon: React.ComponentType<any>;
  href?: string;
  children?: NavLeaf[];
}

const navigation: NavItem[] = [
  { name: 'Dashboard', href: '/admin/dashboard', icon: LayoutDashboard },
  {
    name: 'Enrollment',
    icon: UserPlus,
    children: [
      { name: 'Registrations', href: '/admin/registrations' },
      { name: 'Admissions', href: '/admin/admissions' }
    ]
  },
  {
    name: 'Academics',
    icon: GraduationCap,
    children: [
      { name: 'Student Management', href: '/admin/students' },
      { name: 'Teacher Management', href: '/admin/teachers' },
      { name: 'Class Management', href: '/admin/class-management' }
    ]
  },
  {
    name: 'Academic Calendar',
    icon: CalendarDays,
    children: [
      { name: 'Timetable', href: '/admin/timetable' },
      { name: 'Holiday Calendar', href: '/admin/holiday-calendar' }
    ]
  },
  {
    name: 'Fee Management',
    icon: IndianRupee,
    children: [
      { name: 'Collect Fee', href: '/admin/fees/collect' },
      { name: 'Generate Bill', href: '/admin/fees/generate' },
      { name: 'Transactions', href: '/admin/fees/transactions' },
      { name: 'Fee Structures', href: '/admin/fees/structures' }
    ]
  },
  {
    name: 'Notifications',
    icon: Bell,
    children: [
      { name: 'Notices', href: '/admin/notifications/notices' },
      { name: 'Send Notification', href: '/admin/notifications/send' },
      { name: 'Templates', href: '/admin/notifications/templates' },
      { name: 'History', href: '/admin/notifications/history' },
      { name: 'Settings', href: '/admin/notifications/settings' }
    ]
  },
  {
    name: 'Settings',
    icon: Settings,
    children: [
      { name: 'Classes & Sections', href: '/admin/settings/classes' },
      { name: 'Subjects', href: '/admin/settings/subjects' },
      { name: 'Registration Settings', href: '/admin/settings/registration' },
      { name: 'Academic Year', href: '/admin/settings/academic-year' }
    ]
  }
];

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose, isCollapsed, onToggleCollapse }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  useEffect(() => {
    const activeParent = navigation.find(item =>
      item.children?.some(child => location.pathname === child.href || location.pathname.startsWith(child.href))
    );
    setOpenMenu(activeParent ? activeParent.name : null);
  }, [location.pathname]);

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-20 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed top-0 left-0 z-30 ${isCollapsed ? 'w-20' : 'w-64'} h-full bg-[#211c15] border-r border-[#3a3122] transform transition-all duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 lg:px-6 border-b border-[#3a3122] h-[73px]">
            <div className={`flex items-center ${isCollapsed ? 'justify-center w-full' : 'space-x-3'}`}>
              <div className={`${isCollapsed ? 'w-10 h-10' : 'w-8 h-8'} bg-[#e2916d] rounded-lg flex items-center justify-center flex-shrink-0`}>
                <School className={`${isCollapsed ? 'w-6 h-6' : 'w-5 h-5'} text-[#241408]`} />
              </div>
              {!isCollapsed && (
                <div>
                  <h1 className="text-lg font-semibold text-[#f3ede2]">Kaksha360</h1>
                  <p className="text-xs text-[#b9ad9b]">Admin Portal</p>
                </div>
              )}
            </div>
            {!isCollapsed && (
              <div className="flex items-center space-x-2">
                {/* Collapse button - only show on desktop */}
                <button
                  onClick={onToggleCollapse}
                  className="hidden lg:flex text-[#8a7f6e] hover:text-[#b9ad9b] p-1"
                  title="Collapse sidebar"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                {/* Close button - only show on mobile */}
                <button
                  onClick={onClose}
                  className="lg:hidden text-[#8a7f6e] hover:text-[#b9ad9b] p-1"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            )}
            {isCollapsed && (
              <button
                onClick={onToggleCollapse}
                className="hidden lg:flex text-[#8a7f6e] hover:text-[#b9ad9b] p-1 absolute top-3 right-2"
                title="Expand sidebar"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 overflow-y-auto">
            <ul className="space-y-1.5">
              {navigation.map((item) => {
                if (item.children) {
                  const isChildActive = item.children.some(
                    child => location.pathname === child.href || location.pathname.startsWith(child.href)
                  );
                  const isOpen = openMenu === item.name;

                  return (
                    <li key={item.name}>
                      <button
                        type="button"
                        onClick={() => {
                          if (isCollapsed) {
                            navigate(item.children![0].href);
                            onClose();
                          } else {
                            setOpenMenu(prev => (prev === item.name ? null : item.name));
                          }
                        }}
                        className={`w-full flex items-center ${
                          isCollapsed ? 'justify-center px-2 py-3.5' : 'justify-between px-3 py-2.5'
                        } rounded-[10px] text-sm font-medium transition-colors duration-200 group relative ${
                          isChildActive ? 'bg-[#332217] text-[#e2916d]' : 'text-[#b9ad9b] hover:text-[#f3ede2] hover:bg-[#28221a]'
                        }`}
                        title={isCollapsed ? item.name : undefined}
                      >
                        <span className={`flex items-center ${isCollapsed ? '' : 'space-x-3'}`}>
                          <item.icon className={`${isCollapsed ? 'w-6 h-6' : 'w-5 h-5'} flex-shrink-0`} />
                          {!isCollapsed && <span>{item.name}</span>}
                        </span>
                        {!isCollapsed && (
                          <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                        )}

                        {/* Tooltip for collapsed state */}
                        {isCollapsed && (
                          <div className="absolute left-full ml-2 px-3 py-2 bg-[#28221a] border border-[#3a3122] text-[#f3ede2] text-sm rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50 pointer-events-none shadow-lg">
                            {item.name}
                          </div>
                        )}
                      </button>

                      {!isCollapsed && isOpen && (
                        <ul className="mt-1 ml-[22px] pl-4 border-l border-[#3a3122] space-y-1">
                          {item.children.map(child => {
                            const isLeafActive = location.pathname === child.href || location.pathname.startsWith(child.href);
                            return (
                              <li key={child.name}>
                                <NavLink
                                  to={child.href}
                                  onClick={onClose}
                                  className={`block px-3 py-2 rounded-lg text-sm transition-colors duration-200 ${
                                    isLeafActive ? 'text-[#e2916d] bg-[#332217]' : 'text-[#8a7f6e] hover:text-[#f3ede2] hover:bg-[#28221a]'
                                  }`}
                                >
                                  {child.name}
                                </NavLink>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                }

                const isActive = location.pathname === item.href ||
                  (item.href !== '/admin/dashboard' && location.pathname.startsWith(item.href!));

                return (
                  <li key={item.name}>
                    <NavLink
                      to={item.href!}
                      onClick={onClose}
                      className={`flex items-center ${isCollapsed ? 'justify-center px-2 py-3.5' : 'space-x-3 px-3 py-2.5'} rounded-[10px] text-sm font-medium transition-colors duration-200 group relative ${
                        isActive
                          ? 'bg-[#332217] text-[#e2916d]'
                          : 'text-[#b9ad9b] hover:text-[#f3ede2] hover:bg-[#28221a]'
                      }`}
                      title={isCollapsed ? item.name : undefined}
                    >
                      <item.icon className={`${isCollapsed ? 'w-6 h-6' : 'w-5 h-5'} flex-shrink-0`} />
                      {!isCollapsed && <span>{item.name}</span>}

                      {/* Tooltip for collapsed state */}
                      {isCollapsed && (
                        <div className="absolute left-full ml-2 px-3 py-2 bg-[#28221a] border border-[#3a3122] text-[#f3ede2] text-sm rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50 pointer-events-none shadow-lg">
                          {item.name}
                        </div>
                      )}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
